import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Activity,
  AlertTriangle,
  BarChart3,
  CalendarDays,
  Plus,
  Receipt,
  ScanLine,
  Ticket,
  TrendingUp,
  UserCheck,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { formatEventDate, formatPrice } from "@/components/site/EventCard";
import DayCapacityBars from "@/components/admin/DayCapacityBars";
import PlatformCostsPanel from "@/components/admin/PlatformCostsPanel";
import SalesChart from "@/components/admin/SalesChart";

// Same tile shape as AdminReports.jsx's own KPI component -- kept local to this
// page rather than shared, matching this codebase's existing per-page KPI
// convention (AdminReports.jsx already duplicates this exact pattern).
function KPI({ icon: Icon, label, value, sub, testid }) {
  return (
    <div data-testid={testid} className="bg-svj-card border border-svj-border rounded-2xl p-5">
      <div className="flex items-center gap-2 text-slate-400">
        <Icon className="h-4 w-4 text-svj-cyan" />
        <span className="text-[11px] uppercase tracking-widest font-semibold">{label}</span>
      </div>
      <div className="mt-3 text-3xl font-extrabold text-white">{value}</div>
      {sub && <div className="mt-1 text-xs text-slate-400">{sub}</div>}
    </div>
  );
}

// Same status badge dictionaries as AdminEvents.jsx -- duplicated locally
// rather than shared, same reasoning as KPI above.
const STATUS_STYLES = {
  draft: "bg-slate-500/15 border-slate-500/40 text-slate-300",
  published: "bg-emerald-500/15 border-emerald-500/40 text-emerald-300",
  sold_out: "bg-svj-orange/15 border-svj-orange/40 text-svj-orange",
  cancelled: "bg-red-500/15 border-red-500/40 text-red-300",
  archived: "bg-svj-border/40 border-svj-border text-slate-400",
};
const STATUS_LABELS = {
  draft: "Concept",
  published: "Gepubliceerd",
  sold_out: "Uitverkocht",
  cancelled: "Geannuleerd",
  archived: "Gearchiveerd",
};

function StatusBadge({ status }) {
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest ${STATUS_STYLES[status] || STATUS_STYLES.draft}`}>
      {STATUS_LABELS[status] || status}
    </span>
  );
}

function QuickAction({ to, icon: Icon, label, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="flex items-center gap-3 rounded-xl bg-svj-card border border-svj-border px-4 py-3 text-sm font-semibold text-slate-200 hover:text-white hover:border-svj-cyan/50 transition-colors"
    >
      <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-svj-bg border border-svj-border text-svj-cyan shrink-0">
        <Icon className="h-4 w-4" />
      </span>
      {label}
    </Link>
  );
}

export default function AdminDashboard() {
  const { authAxios, me } = useAuth();
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState({ paid_orders: 0, tickets_sold: 0, revenue_cents: 0 });
  const [loading, setLoading] = useState(true);
  const [openIncidentCount, setOpenIncidentCount] = useState(0);
  const [dayCapacityByEvent, setDayCapacityByEvent] = useState({});
  const isEventUser = me?.role === "event_user";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await authAxios.get("/admin/reports");
        if (cancelled) return;
        setItems(data.items || []);
        setTotals(data.totals || { paid_orders: 0, tickets_sold: 0, revenue_cents: 0 });
      } catch (e) {
        if (!cancelled) toast.error(e?.response?.data?.detail || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    // Best-effort — the sidebar badge already covers the case where this
    // fails/isn't applicable; a dashboard tile just mirrors it, never blocks.
    authAxios
      .get("/admin/incidents/summary")
      .then(({ data }) => { if (!cancelled) setOpenIncidentCount(data.open_count || 0); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [authAxios]);

  const publishedCount = useMemo(() => items.filter((ev) => ev.status === "published" || ev.status === "sold_out").length, [items]);
  const draftEvents = useMemo(() => items.filter((ev) => ev.status === "draft"), [items]);

  const upcoming = useMemo(() => {
    const now = Date.now();
    return items
      .filter((ev) => (ev.status === "published" || ev.status === "sold_out") && ev.starts_at)
      .filter((ev) => new Date(ev.starts_at).getTime() >= now || (ev.ends_at && new Date(ev.ends_at).getTime() >= now))
      .sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at))
      .slice(0, 6);
  }, [items]);

  // Per-day capacity meters (the same ones shown on the Presentie page) for any
  // upcoming multi-day registration event (e.g. a Lichtjestour-style event with
  // its 3 "dorpen"/dagen) -- fetched once per event id from the exact same
  // endpoint MultiDayPresence.jsx already uses, so there is no second source of
  // truth for registered_count/capacity.
  const multiDayUpcomingIds = useMemo(
    () => upcoming.filter((ev) => ev.multi_day_registration_enabled).map((ev) => ev.id),
    [upcoming],
  );

  useEffect(() => {
    const missing = multiDayUpcomingIds.filter((id) => !(id in dayCapacityByEvent));
    if (missing.length === 0) return;
    let cancelled = false;
    missing.forEach((id) => {
      authAxios
        .get(`/admin/events/${id}/registrations`)
        .then(({ data }) => {
          if (!cancelled) setDayCapacityByEvent((prev) => ({ ...prev, [id]: data.days || [] }));
        })
        .catch(() => {
          if (!cancelled) setDayCapacityByEvent((prev) => ({ ...prev, [id]: [] }));
        });
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [multiDayUpcomingIds]);

  const scanTotals = useMemo(
    () =>
      items.reduce(
        (acc, ev) => ({
          scanned: acc.scanned + (ev.stats?.tickets_scanned || 0),
          sold: acc.sold + (ev.stats?.tickets_sold || 0),
        }),
        { scanned: 0, sold: 0 },
      ),
    [items],
  );
  const scanRate = scanTotals.sold ? Math.round((scanTotals.scanned / scanTotals.sold) * 100) : null;

  // Same gating flags AdminLayout.jsx computes for the sidebar (StapKompas/
  // multi-day-registration sub-features) -- recomputed locally from the same
  // already-fetched /admin/reports items rather than sharing state, matching
  // this app's existing per-page-fetches-what-it-needs convention.
  const hasCompanion = items.some((ev) => ev.companion_app_enabled);
  const hasMultiDay = items.some((ev) => ev.multi_day_registration_enabled);

  return (
    <div data-testid="admin-dashboard-page">
      <div className="text-xs uppercase tracking-widest text-svj-cyan font-semibold">Beheer</div>
      <h1 className="mt-2 text-3xl sm:text-4xl font-extrabold text-white">
        Welkom{me?.first_name ? `, ${me.first_name}` : ""}
      </h1>
      <p className="mt-1 text-sm text-slate-400">
        {isEventUser
          ? "Actuele status van je event."
          : me?.is_superadmin
          ? "Actuele status over alle organisaties."
          : `Actuele status van ${me?.organization?.name || "je organisatie"}.`}
      </p>

      {loading ? (
        <div className="mt-8 py-16 text-center text-slate-400" data-testid="admin-dashboard-loading">
          Laden…
        </div>
      ) : items.length === 0 ? (
        <div className="mt-8 rounded-2xl border border-svj-border bg-svj-card p-10 text-center" data-testid="admin-dashboard-empty">
          <CalendarDays className="h-8 w-8 text-svj-cyan mx-auto" />
          <div className="mt-3 text-white font-semibold">Nog geen events</div>
          <p className="mt-1 text-sm text-slate-400">Maak je eerste event aan om hier cijfers te zien.</p>
          {!isEventUser && (
            <Link
              to="/admin/events/new"
              className="mt-5 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold py-2.5 px-5 text-sm hover:shadow-[0_0_24px_rgba(214,61,122,0.4)] transition-all"
            >
              <Plus className="h-4 w-4" /> Nieuw event
            </Link>
          )}
        </div>
      ) : (
        <>
          {/* KPIs */}
          <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-dashboard-kpis">
            <KPI icon={CalendarDays} label="Gepubliceerd" value={publishedCount} sub={`${items.length} event(en) totaal`} testid="dashboard-kpi-events" />
            <KPI icon={Ticket} label="Tickets verkocht" value={totals.tickets_sold || 0} testid="dashboard-kpi-tickets" />
            <KPI icon={TrendingUp} label="Omzet" value={formatPrice(totals.revenue_cents || 0)} sub="incl. btw" testid="dashboard-kpi-revenue" />
            <KPI
              icon={ScanLine}
              label="Gescand"
              value={scanRate === null ? "—" : `${scanRate}%`}
              sub={scanTotals.sold ? `${scanTotals.scanned} / ${scanTotals.sold}` : "nog geen tickets"}
              testid="dashboard-kpi-scanned"
            />
          </div>

          <div className="mt-10">
            <SalesChart
              endpoint="/admin/reports/sales-timeseries"
              authAxios={authAxios}
              title={isEventUser ? "Verkopen over tijd" : "Verkopen over tijd (alle events)"}
              testid="dashboard-sales-chart"
            />
          </div>

          <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Upcoming events */}
            <div className="lg:col-span-2">
              <div className="text-sm font-semibold text-white uppercase tracking-wider mb-3">Eerstvolgende events</div>
              <div className="rounded-2xl border border-svj-border overflow-hidden">
                {upcoming.length === 0 && (
                  <div className="p-8 text-center text-sm text-slate-400" data-testid="dashboard-upcoming-empty">
                    Geen aankomende gepubliceerde events.
                  </div>
                )}
                {upcoming.map((ev) => {
                  const rate = ev.stats?.tickets_sold ? Math.round((ev.stats.tickets_scanned / ev.stats.tickets_sold) * 100) : 0;
                  const dayCapacity = ev.multi_day_registration_enabled ? dayCapacityByEvent[ev.id] : null;
                  return (
                    <div key={ev.id} className="border-b border-svj-border last:border-b-0 bg-svj-card">
                      <Link
                        to={`/admin/events/${ev.id}`}
                        data-testid={`dashboard-upcoming-${ev.id}`}
                        className="flex items-center gap-4 px-5 py-4 hover:bg-svj-bg/60 transition-colors"
                      >
                        <div className="w-10 h-10 rounded-lg bg-svj-bg border border-svj-border overflow-hidden shrink-0">
                          {ev.cover_image_url && <img src={ev.cover_image_url} alt="" className="w-full h-full object-cover" />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <div className="text-white font-semibold truncate">{ev.title}</div>
                            <StatusBadge status={ev.status} />
                          </div>
                          <div className="text-xs text-slate-400 truncate">
                            {formatEventDate(ev.starts_at)}
                            {ev.organizations?.name ? ` · ${ev.organizations.name}` : ""}
                            {ev.location ? ` · ${ev.location}` : ""}
                          </div>
                          <div className="mt-2 flex items-center gap-3">
                            <div className="flex-1 h-1.5 bg-svj-bg border border-svj-border rounded-full overflow-hidden max-w-[160px]">
                              <div
                                className="h-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple"
                                style={{ width: `${Math.min(100, rate)}%` }}
                              />
                            </div>
                            <span className="text-[11px] text-slate-400 shrink-0">
                              {ev.stats?.tickets_sold || 0} verkocht · {rate}% gescand
                            </span>
                          </div>
                        </div>
                      </Link>
                      {dayCapacity && dayCapacity.length > 0 && (
                        <div className="px-5">
                          <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-2">Per dag</div>
                          <DayCapacityBars days={dayCapacity} testid={`dashboard-day-capacity-${ev.id}`} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {draftEvents.length > 0 && (
                <div className="mt-8">
                  <div className="text-sm font-semibold text-white uppercase tracking-wider mb-3">
                    Concepten — nog niet gepubliceerd ({draftEvents.length})
                  </div>
                  <div className="rounded-2xl border border-svj-border overflow-hidden">
                    {draftEvents.map((ev) => (
                      <Link
                        key={ev.id}
                        to={`/admin/events/${ev.id}`}
                        data-testid={`dashboard-draft-${ev.id}`}
                        className="flex items-center justify-between gap-4 px-5 py-3 border-b border-svj-border last:border-b-0 bg-svj-card hover:bg-svj-bg/60 transition-colors"
                      >
                        <div className="min-w-0">
                          <div className="text-white font-semibold truncate text-sm">{ev.title}</div>
                          <div className="text-xs text-slate-400 truncate">{ev.organizations?.name}</div>
                        </div>
                        <StatusBadge status={ev.status} />
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Quick actions */}
            <div>
              <div className="text-sm font-semibold text-white uppercase tracking-wider mb-3">Snelkoppelingen</div>
              <div className="flex flex-col gap-2">
                {!isEventUser && <QuickAction to="/admin/events/new" icon={Plus} label="Nieuw event" testid="dashboard-action-new-event" />}
                <QuickAction to="/admin/events" icon={CalendarDays} label="Alle events" testid="dashboard-action-events" />
                <QuickAction to="/admin/scan" icon={ScanLine} label="Scannen" testid="dashboard-action-scan" />
                {(hasCompanion || hasMultiDay) && (
                  <QuickAction to="/admin/presence" icon={UserCheck} label="Presentie" testid="dashboard-action-presence" />
                )}
                <QuickAction to="/admin/reports" icon={BarChart3} label="Rapportage" testid="dashboard-action-reports" />
                {hasCompanion && (
                  <QuickAction
                    to="/admin/incidents"
                    icon={AlertTriangle}
                    label={openIncidentCount > 0 ? `Incidenten (${openIncidentCount})` : "Incidenten"}
                    testid="dashboard-action-incidents"
                  />
                )}
                {hasCompanion && <QuickAction to="/admin/live-flow" icon={Activity} label="Live Flow" testid="dashboard-action-live-flow" />}
              </div>

              <div className="mt-6 bg-svj-card border border-svj-border rounded-2xl p-5">
                <div className="flex items-center gap-2 text-slate-400">
                  <Receipt className="h-4 w-4 text-svj-cyan" />
                  <span className="text-[11px] uppercase tracking-widest font-semibold">Betaalde bestellingen</span>
                </div>
                <div className="mt-2 text-2xl font-extrabold text-white">{totals.paid_orders || 0}</div>
              </div>
            </div>
          </div>

          {me?.is_superadmin && <PlatformCostsPanel authAxios={authAxios} />}
        </>
      )}
    </div>
  );
}
